import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBtc15Live } from '../lib/apiClient';
import type { Btc15LiveResponse, Btc15LiveTick } from '../lib/btc15Types';

const POLL_INTERVAL_MS = 1_500;

export interface Btc15LiveState {
  data: Btc15LiveResponse | undefined;
  /** The round's price path: server minute closes plus every polled spot. */
  path: Btc15LiveTick[];
  isError: boolean;
  isLoading: boolean;
}

/**
 * Polls /api/btc15-live every 1.5s while `enabled` (and the tab is
 * visible — react-query pauses background polling), and builds a smoother
 * price path than the server's 1-minute closes by keeping every spot price
 * seen since the page opened, for the current round only.
 */
export function useBtc15Live(enabled: boolean): Btc15LiveState {
  const query = useQuery({
    queryKey: ['btc15-live'],
    queryFn: getBtc15Live,
    enabled,
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    staleTime: POLL_INTERVAL_MS,
    retry: 1,
  });

  const seen = useRef<{ roundId: string; ticks: Map<number, number> } | null>(null);
  const data = query.data;

  const path = useMemo(() => {
    if (!data) return [];
    if (!seen.current || seen.current.roundId !== data.round.id) {
      seen.current = { roundId: data.round.id, ticks: new Map() };
    }
    const ticks = seen.current.ticks;
    for (const tick of data.ticks) ticks.set(tick.t, tick.price);
    return [...ticks.entries()]
      .map(([t, price]) => ({ t, price }))
      .filter((tick) => tick.t >= data.round.startMs && tick.t <= data.round.closeMs)
      .sort((a, b) => a.t - b.t);
  }, [data]);

  return { data, path, isError: query.isError, isLoading: query.isLoading };
}
